import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { httpsCallable } from 'firebase/functions';
import { functions, db } from '../lib/firebase';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';

interface DiscordMember {
    id: string;
    username: string;
    displayName: string;
    avatar?: string;
    roles: string[];
    joinedAt: string | null;
    isLinked: boolean;
    linkedType: 'student' | 'parent' | 'admin' | 'coach' | null;
    linkedName: string | null;
    linkedRosterId: string | null;
}

interface DiscordRole {
    id: string;
    name: string;
    color: string;
    position: number;
}

interface Player {
    id: string;
    firstName: string;
    lastName: string;
    discordId?: string;
    parentDiscordId?: string;
}

export default function AdminDiscordPage() {
    const { user, userDoc } = useAuth();

    const [members, setMembers] = useState<DiscordMember[]>([]);
    const [_roles, setRoles] = useState<DiscordRole[]>([]);
    const [players, setPlayers] = useState<Player[]>([]);
    const [loading, setLoading] = useState(true);
    const [syncing, setSyncing] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [filter, setFilter] = useState<'all' | 'linked' | 'unlinked'>('all');
    const [stats, setStats] = useState({ total: 0, linked: 0, unlinked: 0 });

    // Link modal state
    const [linkModal, setLinkModal] = useState<{
        open: boolean;
        member: DiscordMember | null;
        selectedPlayer: string;
        linkType: 'student' | 'parent';
        saving: boolean;
    }>({
        open: false,
        member: null,
        selectedPlayer: '',
        linkType: 'student',
        saving: false,
    });

    // Load players for linking
    const loadPlayers = useCallback(async () => {
        try {
            const playersSnap = await getDocs(
                query(collection(db, 'playerRoster'), orderBy('lastName'))
            );
            const playersList = playersSnap.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
            })) as Player[];
            setPlayers(playersList);
        } catch (err) {
            console.error('Error loading players:', err);
        }
    }, []);

    // Load data
    const loadData = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            // Get roles
            const getRoles = httpsCallable(functions, 'getDiscordRoles');
            const rolesResult = await getRoles();
            const rolesData = rolesResult.data as { roles: DiscordRole[]; needsSync: boolean };
            setRoles(rolesData.roles);

            // Get members
            const getMembers = httpsCallable(functions, 'getDiscordMembers');
            const membersResult = await getMembers();
            const membersData = membersResult.data as {
                members: DiscordMember[];
                totalCount: number;
                linkedCount: number;
                unlinkedCount: number;
            };

            setMembers(membersData.members);
            setStats({
                total: membersData.totalCount,
                linked: membersData.linkedCount,
                unlinked: membersData.unlinkedCount,
            });

            if (rolesData.needsSync && membersData.members.length === 0) {
                setError('Discord server not synced yet. Click "Sync Discord Server" to get started.');
            }
        } catch (err: any) {
            console.error('Error loading Discord data:', err);

            // Handle specific error codes
            if (err.code === 'functions/failed-precondition') {
                setError('Discord bot not configured. Please set up the bot token and server ID in Firebase secrets.');
            } else if (err.code === 'functions/not-found') {
                setError('Discord functions not deployed. Please deploy the Firebase functions first.');
            } else if (err.code === 'functions/unauthenticated') {
                setError('Authentication required. Please sign in again.');
            } else if (err.code === 'functions/internal') {
                setError('Discord API error. The bot may not have proper permissions or the server ID may be incorrect.');
            } else if (err.message?.includes('Failed to fetch') || err.message?.includes('network')) {
                setError('Network error. Please check your connection and try again.');
            } else {
                setError(err.message || 'Failed to load Discord data. See console for details.');
            }
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (user && (userDoc?.role === 'admin' || userDoc?.role === 'coach')) {
            loadData();
            loadPlayers();
        }
    }, [user, userDoc, loadData, loadPlayers]);

    // Sync Discord server
    const handleSync = async () => {
        try {
            setSyncing(true);
            setError(null);

            const syncServer = httpsCallable(functions, 'syncDiscordServer');
            const result = await syncServer();
            const data = result.data as { success: boolean; memberCount: number; message: string };

            if (data.success) {
                await loadData();
            }
        } catch (err: any) {
            console.error('Error syncing Discord:', err);
            setError(err.message || 'Failed to sync Discord server');
        } finally {
            setSyncing(false);
        }
    };

    // Open link modal
    const openLinkModal = (member: DiscordMember) => {
        setLinkModal({
            open: true,
            member,
            selectedPlayer: '',
            linkType: 'student',
            saving: false,
        });
    };

    // Handle link/unlink
    const handleLink = async () => {
        if (!linkModal.member) return;

        try {
            setLinkModal(prev => ({ ...prev, saving: true }));

            const linkFn = httpsCallable(functions, 'linkDiscordToPlayer');
            await linkFn({
                discordId: linkModal.member.id,
                playerId: linkModal.selectedPlayer || null,
                linkType: linkModal.linkType,
            });

            // Reload data
            await loadData();
            setLinkModal({ open: false, member: null, selectedPlayer: '', linkType: 'student', saving: false });
        } catch (err: any) {
            console.error('Error linking:', err);
            setError(err.message || 'Failed to link account');
            setLinkModal(prev => ({ ...prev, saving: false }));
        }
    };

    // Filter members
    const filteredMembers = members.filter(m => {
        if (filter === 'linked') return m.isLinked;
        if (filter === 'unlinked') return !m.isLinked;
        return true;
    });

    // Get badge color for linked type
    const getLinkedBadge = (member: DiscordMember) => {
        if (!member.isLinked) {
            return (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-500/20 text-yellow-400">
                    ⚠️ Unverified
                </span>
            );
        }

        const colors: Record<string, string> = {
            student: 'bg-blue-500/20 text-blue-400',
            parent: 'bg-purple-500/20 text-purple-400',
            admin: 'bg-red-500/20 text-red-400',
            coach: 'bg-green-500/20 text-green-400',
        };

        return (
            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${colors[member.linkedType!] || ''}`}>
                {member.linkedType?.charAt(0).toUpperCase()}{member.linkedType?.slice(1)}: {member.linkedName}
            </span>
        );
    };

    // Get available players (not already linked to another Discord account)
    const getAvailablePlayers = () => {
        return players.filter(player => {
            // For student link type, filter out players already linked to a Discord
            if (linkModal.linkType === 'student') {
                return !player.discordId || player.discordId === linkModal.member?.id;
            }
            // For parent link type, filter out players already linked to a parent Discord
            return !player.parentDiscordId || player.parentDiscordId === linkModal.member?.id;
        });
    };

    if (loading) {
        return (
            <div className="p-8 flex items-center justify-center">
                <div className="w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
        );
    }

    return (
        <div className="p-6 lg:p-8 max-w-5xl">
            {/* Header */}
            <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
                <div>
                    <h1 className="text-3xl font-display font-bold text-white">Discord Management</h1>
                    <p className="text-secondary-400">Manage Discord server members and account linking</p>
                </div>
                <button
                    onClick={handleSync}
                    disabled={syncing}
                    className="btn-primary flex items-center gap-2"
                >
                    {syncing ? (
                        <>
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            Syncing...
                        </>
                    ) : (
                        <>
                            🔄 Sync Discord Server
                        </>
                    )}
                </button>
            </div>

            {/* Error Message */}
            {error && (
                <div className="mb-6 p-4 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400">
                    {error}
                </div>
            )}

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                <div className="card p-4">
                    <div className="text-3xl font-bold text-white">{stats.total}</div>
                    <div className="text-secondary-400">Total Members</div>
                </div>
                <div className="card p-4">
                    <div className="text-3xl font-bold text-green-400">{stats.linked}</div>
                    <div className="text-secondary-400">Linked Accounts</div>
                </div>
                <div className="card p-4">
                    <div className="text-3xl font-bold text-yellow-400">{stats.unlinked}</div>
                    <div className="text-secondary-400">Unverified</div>
                </div>
            </div>

            {/* Filters */}
            <div className="flex gap-2 mb-6">
                {(['all', 'linked', 'unlinked'] as const).map(f => (
                    <button
                        key={f}
                        onClick={() => setFilter(f)}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${filter === f
                            ? 'bg-primary-500 text-white'
                            : 'bg-secondary-800 text-secondary-300 hover:bg-secondary-700'
                            }`}
                    >
                        {f === 'all' ? 'All Members' : f === 'linked' ? 'Linked' : 'Unverified'}
                    </button>
                ))}
            </div>

            {/* Members Table */}
            <div className="card overflow-x-auto">
                <table className="w-full">
                    <thead>
                        <tr className="border-b border-secondary-700">
                            <th className="text-left py-3 px-3 text-secondary-400 font-medium">Member</th>
                            <th className="text-left py-3 px-3 text-secondary-400 font-medium">Linked Account</th>
                            <th className="text-left py-3 px-3 text-secondary-400 font-medium">Discord Roles</th>
                            <th className="text-left py-3 px-3 text-secondary-400 font-medium">Joined</th>
                            <th className="text-left py-3 px-3 text-secondary-400 font-medium">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredMembers.length === 0 ? (
                            <tr>
                                <td colSpan={5} className="py-8 text-center text-secondary-400">
                                    {members.length === 0
                                        ? 'No members synced. Click "Sync Discord Server" to load members.'
                                        : 'No members match the current filter.'}
                                </td>
                            </tr>
                        ) : (
                            filteredMembers.map(member => (
                                <tr key={member.id} className="border-b border-secondary-800 hover:bg-secondary-800/50">
                                    <td className="py-3 px-3">
                                        <div className="flex items-center gap-3">
                                            {member.avatar ? (
                                                <img
                                                    src={member.avatar}
                                                    alt=""
                                                    className="w-8 h-8 rounded-full"
                                                />
                                            ) : (
                                                <div className="w-8 h-8 rounded-full bg-secondary-600 flex items-center justify-center text-secondary-300">
                                                    {member.displayName.charAt(0).toUpperCase()}
                                                </div>
                                            )}
                                            <div>
                                                <div className="font-medium text-white">{member.displayName}</div>
                                                <div className="text-sm text-secondary-400">@{member.username}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="py-3 px-3">
                                        {getLinkedBadge(member)}
                                    </td>
                                    <td className="py-3 px-3">
                                        <div className="flex flex-wrap gap-1">
                                            {member.roles.slice(0, 3).map((roleName, idx) => (
                                                <span
                                                    key={idx}
                                                    className="text-xs bg-secondary-700 px-1.5 py-0.5 rounded text-secondary-300"
                                                >
                                                    {roleName}
                                                </span>
                                            ))}
                                            {member.roles.length > 3 && (
                                                <span className="text-xs text-secondary-500">
                                                    +{member.roles.length - 3} more
                                                </span>
                                            )}
                                        </div>
                                    </td>
                                    <td className="py-3 px-3 text-secondary-400 text-sm">
                                        {member.joinedAt
                                            ? new Date(member.joinedAt).toLocaleDateString()
                                            : '—'}
                                    </td>
                                    <td className="py-3 px-3">
                                        <button
                                            onClick={() => openLinkModal(member)}
                                            className="text-sm text-primary-400 hover:text-primary-300 hover:underline"
                                        >
                                            {member.isLinked ? '✏️ Edit Link' : '🔗 Link to Player'}
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Setup Instructions */}
            {stats.total === 0 && !error && (
                <div className="mt-8 card p-6 bg-secondary-800/50">
                    <h2 className="text-xl font-bold text-white mb-4">Getting Started</h2>
                    <ol className="space-y-3 text-secondary-300">
                        <li className="flex gap-3">
                            <span className="font-bold text-primary-400">1.</span>
                            <span>Create a Discord bot at <a href="https://discord.com/developers/applications" target="_blank" rel="noopener noreferrer" className="text-primary-400 hover:underline">Discord Developer Portal</a></span>
                        </li>
                        <li className="flex gap-3">
                            <span className="font-bold text-primary-400">2.</span>
                            <span>Set the bot token and server ID in Firebase secrets</span>
                        </li>
                        <li className="flex gap-3">
                            <span className="font-bold text-primary-400">3.</span>
                            <span>Invite the bot to your server with the required permissions</span>
                        </li>
                        <li className="flex gap-3">
                            <span className="font-bold text-primary-400">4.</span>
                            <span>Click "Sync Discord Server" to discover roles and members</span>
                        </li>
                    </ol>
                </div>
            )}

            {/* Link Modal */}
            {linkModal.open && linkModal.member && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
                    <div className="bg-secondary-900 rounded-xl p-6 w-full max-w-md border border-secondary-700 shadow-2xl">
                        <h2 className="text-xl font-bold text-white mb-4">
                            Link Discord Account
                        </h2>

                        <div className="flex items-center gap-3 mb-6 p-3 bg-secondary-800 rounded-lg">
                            {linkModal.member.avatar ? (
                                <img
                                    src={linkModal.member.avatar}
                                    alt=""
                                    className="w-10 h-10 rounded-full"
                                />
                            ) : (
                                <div className="w-10 h-10 rounded-full bg-secondary-600 flex items-center justify-center text-secondary-300">
                                    {linkModal.member.displayName.charAt(0).toUpperCase()}
                                </div>
                            )}
                            <div>
                                <div className="font-medium text-white">{linkModal.member.displayName}</div>
                                <div className="text-sm text-secondary-400">@{linkModal.member.username}</div>
                            </div>
                        </div>

                        {/* Link Type */}
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-secondary-300 mb-2">
                                Link As
                            </label>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => setLinkModal(prev => ({ ...prev, linkType: 'student', selectedPlayer: '' }))}
                                    className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${linkModal.linkType === 'student'
                                        ? 'bg-blue-500 text-white'
                                        : 'bg-secondary-700 text-secondary-300 hover:bg-secondary-600'
                                        }`}
                                >
                                    🎮 Student
                                </button>
                                <button
                                    onClick={() => setLinkModal(prev => ({ ...prev, linkType: 'parent', selectedPlayer: '' }))}
                                    className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${linkModal.linkType === 'parent'
                                        ? 'bg-purple-500 text-white'
                                        : 'bg-secondary-700 text-secondary-300 hover:bg-secondary-600'
                                        }`}
                                >
                                    👨‍👩‍👧 Parent
                                </button>
                            </div>
                        </div>

                        {/* Player Selection */}
                        <div className="mb-6">
                            <label className="block text-sm font-medium text-secondary-300 mb-2">
                                {linkModal.linkType === 'student' ? 'Select Player' : 'Select Player (Parent Of)'}
                            </label>
                            <select
                                value={linkModal.selectedPlayer}
                                onChange={(e) => setLinkModal(prev => ({ ...prev, selectedPlayer: e.target.value }))}
                                className="w-full px-3 py-2 bg-secondary-800 border border-secondary-600 rounded-lg text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            >
                                <option value="">— Select a player —</option>
                                {getAvailablePlayers().map(player => (
                                    <option key={player.id} value={player.id}>
                                        {player.firstName} {player.lastName}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Current Link Info */}
                        {linkModal.member.isLinked && (
                            <div className="mb-4 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg text-sm text-yellow-400">
                                ⚠️ Currently linked as {linkModal.member.linkedType}: {linkModal.member.linkedName}
                            </div>
                        )}

                        {/* Actions */}
                        <div className="flex gap-3">
                            <button
                                onClick={() => setLinkModal({ open: false, member: null, selectedPlayer: '', linkType: 'student', saving: false })}
                                className="flex-1 px-4 py-2 bg-secondary-700 text-secondary-300 rounded-lg hover:bg-secondary-600 transition-colors"
                                disabled={linkModal.saving}
                            >
                                Cancel
                            </button>
                            {linkModal.member.isLinked && (
                                <button
                                    onClick={() => {
                                        setLinkModal(prev => ({ ...prev, selectedPlayer: '' }));
                                        handleLink();
                                    }}
                                    className="px-4 py-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors"
                                    disabled={linkModal.saving}
                                >
                                    Unlink
                                </button>
                            )}
                            <button
                                onClick={handleLink}
                                disabled={!linkModal.selectedPlayer || linkModal.saving}
                                className="flex-1 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                            >
                                {linkModal.saving ? (
                                    <>
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                        Saving...
                                    </>
                                ) : (
                                    'Link Account'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
